import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Flame, CalendarClock, Dices, Camera, LineChart } from 'lucide-react';
import { getExplainerPage } from '../../content/explanations';
import {
    FireCrossoverIllustration,
    TimelineStoryIllustration,
    SimulationStoryIllustration,
    SnapshotsIllustration,
} from './illustrations';
import useMediaQuery from '../../hooks/useMediaQuery';
import useHistoryBack from '../../hooks/useHistoryBack';
import './explain.css';

const PAGE_ICONS = { flame: Flame, calendar: CalendarClock, dice: Dices, camera: Camera };

const PageIcon = ({ name, size = 26 }) => {
    const Icon = PAGE_ICONS[name] || LineChart;
    return <Icon size={size} />;
};

const ANIMATION_MS = 280;

/* ------------------------------------------------------------------ bodies
   Each feature gets a bespoke story: its own animation and its own prose.
   Shared bits: `exp-stage` staggers blocks in; `exp-page-hero` frames SVGs. */

const FireBody = ({ t }) => (
    <>
        <div className="exp-hero exp-stage" style={{ '--stage': 1 }}>
            <FireCrossoverIllustration
                label={t('explain.pages.fire.title')}
                texts={{
                    assets: t('explain.pages.fire.chart.assets'),
                    target: t('explain.pages.fire.chart.target'),
                    freedom: t('explain.pages.fire.chart.freedom'),
                }}
            />
        </div>
        <div className="exp-stage" style={{ '--stage': 2 }}>
            <span className="field-label">{t('explain.pages.fire.methodsTitle')}</span>
            <div className="exp-methods">
                <div className="exp-method fire">
                    <b>{t('explain.pages.fire.methods.fire.name')}</b>
                    <p>{t('explain.pages.fire.methods.fire.desc')}</p>
                </div>
                <div className="exp-method income">
                    <b>{t('explain.pages.fire.methods.income.name')}</b>
                    <p>{t('explain.pages.fire.methods.income.desc')}</p>
                </div>
            </div>
        </div>
        <p className="exp-story exp-stage" style={{ '--stage': 3 }}>{t('explain.pages.fire.story')}</p>
    </>
);

const TimelineBody = ({ t }) => (
    <>
        <div className="exp-hero exp-stage" style={{ '--stage': 1 }}>
            <TimelineStoryIllustration
                label={t('explain.pages.timeline.title')}
                texts={{
                    e1: t('explain.pages.timeline.chart.e1'),
                    e2: t('explain.pages.timeline.chart.e2'),
                    e3: t('explain.pages.timeline.chart.e3'),
                }}
            />
            <div className="exp-legend">
                <span className="exp-legend-item"><span className="exp-legend-line" style={{ background: 'var(--accent)' }} />{t('explain.pages.timeline.chart.with')}</span>
                <span className="exp-legend-item"><span className="exp-legend-line exp-legend-line--dashed" />{t('explain.pages.timeline.chart.without')}</span>
            </div>
        </div>
        <p className="exp-story exp-stage" style={{ '--stage': 2 }}>{t('explain.pages.timeline.story')}</p>
    </>
);

const SimulationBody = ({ t }) => (
    <>
        <div className="exp-hero exp-stage" style={{ '--stage': 1 }}>
            <SimulationStoryIllustration
                label={t('explain.pages.simulation.title')}
                texts={{
                    median: t('explain.pages.simulation.chart.median'),
                    band: t('explain.pages.simulation.chart.band'),
                }}
            />
        </div>
        <p className="exp-story exp-stage" style={{ '--stage': 2 }}>{t('explain.pages.simulation.story')}</p>
    </>
);

const SnapshotsBody = ({ t }) => (
    <>
        <div className="exp-hero exp-stage" style={{ '--stage': 1 }}>
            <SnapshotsIllustration
                label={t('explain.pages.snapshots.title')}
                texts={{
                    then: t('explain.pages.snapshots.chart.then'),
                    now: t('explain.pages.snapshots.chart.now'),
                }}
            />
        </div>
        <ol className="exp-beats">
            {[1, 2, 3].map((n, i) => (
                <li className="exp-beat exp-stage" style={{ '--stage': 2 + i }} key={n}>
                    <span className="exp-beat-num">{n}</span>
                    <div>
                        <strong>{t(`explain.pages.snapshots.b${n}.title`)}</strong>
                        <p>{t(`explain.pages.snapshots.b${n}.body`)}</p>
                    </div>
                </li>
            ))}
        </ol>
    </>
);

const BODIES = { fire: FireBody, timeline: TimelineBody, simulation: SimulationBody, snapshots: SnapshotsBody };

/* ------------------------------------------------------------------ shell */

// F25 — full-screen feature explainer in the onboarding design language.
//
// Keep this component MOUNTED and toggle `isOpen` (like ModalDrawer/FirePage) so
// the slide-out close animation can play. Content itself mounts only while open,
// so the SVG draw-in and stagger animations restart every time.
const ExplainPage = ({ feature, isOpen, onClose }) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [isMounted, setIsMounted] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    // The host may clear `feature` the moment it closes; keep the last one so
    // the exit animation still has content to render.
    const lastFeatureRef = useRef(feature);
    if (feature) lastFeatureRef.current = feature;

    useHistoryBack(isOpen && isMobile, onClose, true, `explainPage-${lastFeatureRef.current || 'none'}`);

    useEffect(() => {
        let openTimer;
        let closeTimer;
        if (isOpen) {
            setIsMounted(true);
            openTimer = setTimeout(() => setIsAnimating(true), 20);
        } else {
            setIsAnimating(false);
            closeTimer = setTimeout(() => setIsMounted(false), ANIMATION_MS);
        }
        return () => { clearTimeout(openTimer); clearTimeout(closeTimer); };
    }, [isOpen]);

    // Escape closes; body scroll locks while open.
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [isOpen, onClose]);

    const entry = getExplainerPage(lastFeatureRef.current);
    if (!isMounted || !entry) return null;

    const Body = BODIES[lastFeatureRef.current] || null;

    // Portal to <body>: the explainer opens from inside containers that create
    // a `position:fixed` containing block (the onboarding step keeps a
    // persisted transform from its `both`-filled entrance animation), which
    // would otherwise trap this overlay and cause a nested scrollbar.
    return createPortal(
        // Variant C's shell: a `.exp-page-wrap` that owns visibility and a
        // `.exp-page` that fades and lifts inside it. The page's own painted
        // background comes with it, so the two hand-rolled blobs go — the demo's
        // .exp-page is opaque by design, because it replaces the page under it
        // rather than floating over it.
        <div className={`exp-page-wrap ${isAnimating ? 'open' : ''}`}>
            <div className="exp-page" role="dialog" aria-modal="true" aria-label={t(entry.titleKey)}>
                <button className="exp-page-close" onClick={onClose} aria-label={t('general.close')}>
                    <X size={20} />
                </button>

                <div className="exp-inner">
                    <div className="exp-top exp-stage" style={{ '--stage': 0 }}>
                        <span className="exp-eyebrow">
                            <span className={`brand-chip ${entry.chip}`}><PageIcon name={entry.icon} /></span>
                            {t(entry.nameKey)}
                        </span>
                    </div>
                    <h1 className="exp-title exp-stage" style={{ '--stage': 0 }}>{t(entry.titleKey)}</h1>
                    <p className="exp-sub exp-stage" style={{ '--stage': 0 }}>{t(entry.subtitleKey)}</p>

                    {Body && <Body t={t} />}

                    <div className="exp-page-cta exp-stage" style={{ '--stage': 5 }}>
                        <button className="btn btn-grad btn-lg" onClick={onClose}>{t('explain.gotIt')}</button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ExplainPage;
